import { SimpleColumn } from "../types";
import { renderEmpty, renderEmailLink, renderPhoneLink } from "./shared";
import { User, Building } from "lucide-react";
import type { QBCustomerMatchStatus, SupabaseContactMinimal } from "@/lib/types/quickbooks-unified";

/**
 * Normalized QuickBooks customer fields the columns read off the matching row's
 * `qb` payload. Hand-rolled (QB API objects are not codegen) — kept to exactly
 * the fields these renderers access.
 */
interface QBCustomerDisplay {
  id?: string | null;
  displayName?: string | null;
  email?: string | null;
  phone?: string | null;
  realmId?: string | null;
}

/** A QuickBooks customer matching row as rendered by the admin entities table. */
interface QBCustomerMatchRow {
  qb?: QBCustomerDisplay;
  status?: QBCustomerMatchStatus | string;
  matches?: SupabaseContactMinimal[];
  candidates?: SupabaseContactMinimal[];
  company?: { companyName?: string };
}

const MATCH_STATUS_COLORS: Record<string, string> = {
  matched: "bg-green-100 text-green-800",
  unmatched: "bg-red-100 text-red-800",
  ambiguous: "bg-yellow-100 text-yellow-800",
};

export function createQBCustomerColumns(_customers: QBCustomerMatchRow[]): SimpleColumn[] {
  const columns: SimpleColumn[] = [];

  columns.push({
    id: "displayName",
    label: "Customer Name",
    category: "basic",
    visible: true,
    priority: 1,
    width: 200,
    render: (_value: unknown, item: QBCustomerMatchRow) => (
      <div className="min-w-0">
        <div className="font-medium text-card-foreground truncate">
          {item.qb?.displayName || "Unknown Customer"}
        </div>
        {item.qb?.email && <div className="text-sm text-gray-500 truncate">{item.qb.email}</div>}
      </div>
    ),
  });

  columns.push({
    id: "email",
    label: "Email",
    category: "contact",
    visible: true,
    priority: 2,
    width: 180,
    render: (_value: unknown, item: QBCustomerMatchRow) => renderEmailLink(item.qb?.email ?? ""),
  });

  columns.push({
    id: "phone",
    label: "Phone",
    category: "contact",
    visible: true,
    priority: 3,
    width: 120,
    render: (_value: unknown, item: QBCustomerMatchRow) => renderPhoneLink(item.qb?.phone ?? ""),
  });

  columns.push({
    id: "status",
    label: "Match Status",
    category: "status",
    visible: true,
    priority: 4,
    width: 100,
    render: (_value: unknown, item: QBCustomerMatchRow) => (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          (item.status && MATCH_STATUS_COLORS[item.status]) || "bg-card-100 text-gray-800"
        }`}
      >
        {item.status || "unknown"}
      </span>
    ),
  });

  columns.push({
    id: "matches",
    label: "Matches",
    category: "integration",
    visible: true,
    priority: 5,
    width: 100,
    render: (_value: unknown, item: QBCustomerMatchRow) => {
      const matchCount = item.matches?.length || 0;
      if (matchCount === 0) return <span className="text-gray-400">No matches</span>;

      return (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-gray-400" />
          <span className="font-medium">{matchCount}</span>
          <span className="text-sm text-gray-500">{matchCount === 1 ? "match" : "matches"}</span>
        </div>
      );
    },
  });

  columns.push({
    id: "matchedContacts",
    label: "Matched Companies",
    category: "integration",
    visible: true,
    priority: 6,
    width: 200,
    render: (_value: unknown, item: QBCustomerMatchRow) => {
      const matches = item.matches ?? [];
      if (matches.length === 0) return <span className="text-gray-400">No matches</span>;

      return (
        <div className="space-y-1">
          {matches.map((match, index) => (
            <div key={match.id ?? index} className="flex items-center gap-2 text-sm">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-card-foreground truncate">
                  {match.name || "Unknown Company"}
                </div>
                {match.company_name && match.company_name !== match.name && (
                  <div className="text-gray-500 truncate">{match.company_name}</div>
                )}
                {match.email && <div className="text-gray-400 truncate">{match.email}</div>}
              </div>
              <a
                href={`/companies/${match.id}`}
                className="text-primary hover:text-primary-dark flex-shrink-0"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Building className="h-4 w-4" />
              </a>
            </div>
          ))}
        </div>
      );
    },
  });

  columns.push({
    id: "qbId",
    label: "QB ID",
    category: "integration",
    visible: false,
    priority: 7,
    width: 120,
    render: (_value: unknown, item: QBCustomerMatchRow) => {
      if (!item.qb?.id) return renderEmpty();
      return <span className="font-mono text-gray-600 text-sm">{item.qb.id}</span>;
    },
  });

  columns.push({
    id: "uniqueId",
    label: "Unique ID",
    category: "integration",
    visible: true,
    priority: 8,
    width: 200,
    render: (_value: unknown, item: QBCustomerMatchRow) => {
      if (!item.qb?.id || !item.qb?.realmId) return renderEmpty();
      return (
        <div className="min-w-0">
          <div className="font-mono text-sm text-card-foreground">{item.qb.id}</div>
          <div className="text-xs text-gray-500 font-mono">+ {item.qb.realmId.slice(-4)}</div>
        </div>
      );
    },
  });

  columns.push({
    id: "realmId",
    label: "Company",
    category: "integration",
    visible: true,
    priority: 9,
    width: 150,
    render: (_value: unknown, item: QBCustomerMatchRow) => {
      if (!item.qb?.realmId) return renderEmpty();
      return (
        <div className="min-w-0">
          <div className="font-medium text-card-foreground truncate">
            {item.company?.companyName || `Company ${item.qb.realmId.slice(-4)}`}
          </div>
          <div className="text-sm text-gray-500 font-mono">{item.qb.realmId}</div>
        </div>
      );
    },
  });

  columns.push({
    id: "candidates",
    label: "Candidates",
    category: "integration",
    visible: false,
    priority: 10,
    width: 120,
    render: (_value: unknown, item: QBCustomerMatchRow) => {
      const candidateCount = item.candidates?.length || 0;
      if (candidateCount === 0) return renderEmpty();

      return (
        <span className="font-medium text-blue-600">
          {candidateCount} {candidateCount === 1 ? "candidate" : "candidates"}
        </span>
      );
    },
  });

  columns.push({
    id: "created_at",
    label: "Created",
    category: "dates",
    visible: false,
    priority: 10,
    width: 120,
    render: () => {
      return <div className="text-sm text-gray-600">Synced</div>;
    },
  });

  columns.push({
    id: "actions",
    label: "Actions",
    category: "actions",
    visible: true,
    priority: 99,
    width: 200,
    render: () => {
      return <div className="text-sm text-gray-400">Actions</div>;
    },
  });

  return columns.sort((a, b) => a.priority - b.priority);
}
